import { some } from "lodash/fp";
import { DateTime } from "luxon";

import { parseUTCDateTimeFromString } from "@app/utils/utils";

import type {
  ApiSessionsListEntity,
  BillingPlan,
  SessionInfo,
  UserMeeting,
} from "../types";

type Session = SessionInfo | UserMeeting | ApiSessionsListEntity;

export const isReadLite = (session?: Session): boolean =>
  session?.metricsLevel === "lite";

/** Active or pre-billing-based-processing sessions only - whether the session is out-of-quota */
export const isOutOfQuota = (session?: Session): boolean =>
  session?.billingState === "quota_exceeded";

export const isProcessed = (session?: Session): boolean =>
  session?.processingStatus === "completed";

/** Whether the report is out-of-quota (i.e., has restricted metrics) */
export const isRestricted = (session?: Session): boolean => {
  if (isProcessed(session)) {
    // prior to billing-based processing, reports were fully processed even if
    // they were out of quota. use the upsell preview flag (is backwards-compatible
    // in BE) to determine if metrics are restricted, as the billing state will be
    // "quota_exceeded" both before and after a user upgrades their plan, so we can't
    // rely on that alone.
    return Boolean(session?.isUpsellPreview);
  }

  // in the billing-based processing world, out-of-quota sessions will have a null
  // processing state, so we can simply key off the billing state to determine if
  // it's out of quota (billing state will get updated when the user upgrades their
  return isOutOfQuota(session);
};

export const isTerminated = (session?: Session): boolean => {
  if (!session) return false;

  return (
    session.state === "ended" ||
    session.state === "cancelled" ||
    session.state === "error" ||
    session.state === "uploaded"
  );
};

// because notetaker sessions don't currently "end" in the same way as other sessions,
// there's not any distinction between the session being active or inactive, it just is
export const isActive = (session?: Session): boolean =>
  !isTerminated(session) &&
  !isNotetaker(session) &&
  !isUploadedSession(session);

export const isProcessing = (session?: Session): boolean =>
  session?.processingStatus === "pending" || isActive(session);

export const isDataDeleted = (session?: Session): boolean => {
  if (isProcessed(session) || !session?.dataExpirationDate) return false;

  return (
    parseUTCDateTimeFromString(session.dataExpirationDate) < DateTime.now()
  );
};

export const isNotetaker = (session?: Session): boolean => {
  return session?.metricsLevel === "no_metrics" || session?.state === "manual";
};

export const isUploadedSession = (session?: Session): boolean => {
  return (
    session?.state === "uploaded" || session?.billingState === "upload_credits"
  );
};

export const isOverMaxSessionLength = (session?: Session): boolean => {
  return (
    (session?.billingState !== "quota_exceeded" || !session?.billingState) &&
    some({ kind: "max_session_length" }, session?.alerts)
  );
};

export const isUnsupportedLanguage = (session?: Session): boolean => {
  return some({ kind: "invalid_language" }, session?.alerts);
};

/* 
  Used to determine plan type at time of session for recording caps.
  In quota exceeded sessions, plan type and recording cap are irrelevant
*/
export const getSessionBillingPlanByBillingState = (
  session?: Session
): BillingPlan | undefined => {
  return session?.billingState === "free_trial"
    ? "enterprise"
    : isOutOfQuota(session) || isUploadedSession(session)
      ? undefined
      : (session?.billingState as BillingPlan);
};
